/**
 * Automatic analytics tracking for auth, routing, HTTP, and mobile events.
 *
 * Uses minimal interfaces so there's no hard dependency on
 * `@molecule/app-auth`, `@molecule/app-routing`, `@molecule/app-http`,
 * `@molecule/app-lifecycle`, or `@molecule/app-push`.
 *
 * @module
 */
import { getProvider } from './provider.js';
export function setupAutoTracking(options) {
    const cleanups = [];
    // Resolve the provider per event — capturing getProvider() once at setup
    // permanently baked in the no-op provider whenever setupAutoTracking() ran
    // before the analytics bond was set, silently dropping every event forever.
    const analytics = () => getProvider();
    /**
     * The identity to record for a profile: the app's own extractor when given,
     * otherwise the default `id`/`email`/`name` fields. `null` means this session
     * has no identity to record — no `identify`, and no `userId` on the event.
     *
     * @param user - The profile a sign-in event carried.
     * @returns The identity, or `null` when none can be read.
     */
    const identityOf = (user) => {
        if (options.identifyUser)
            return options.identifyUser(user);
        if (!('id' in user) || typeof user.id !== 'string' || user.id === '')
            return null;
        const identity = { userId: user.id };
        if ('email' in user && typeof user.email === 'string')
            identity.email = user.email;
        if ('name' in user && typeof user.name === 'string')
            identity.name = user.name;
        return identity;
    };
    /**
     * Identifies the signed-in user and tracks the sign-in event.
     *
     * When there is no identity ({@link IdentifyUser} returned `null`, or the
     * profile carries no readable `id`), the `identify` call is skipped and the
     * sign-in is STILL tracked — without a `userId` — so `auth.login` and
     * `auth.register` counts are not silently lost for anonymous sessions.
     *
     * @param name - `auth.login` or `auth.register`.
     * @param user - The profile the event carried.
     */
    const trackSignIn = (name, user) => {
        if (!user)
            return;
        const identity = identityOf(user);
        if (identity) {
            analytics()
                .identify(identity)
                .catch(() => { });
            analytics()
                .track({ name, properties: { userId: identity.userId } })
                .catch(() => { });
            return;
        }
        // No identity means the app said this session has none (or the profile has
        // no id and no extractor reached this client): never send `userId:
        // undefined`, but do not drop the sign-in event either.
        analytics()
            .track({ name })
            .catch(() => { });
    };
    // Auth events
    if (options.authClient) {
        const removeListener = options.authClient.addEventListener((event) => {
            switch (event.type) {
                case 'login':
                    trackSignIn('auth.login', event.user);
                    break;
                case 'register':
                    trackSignIn('auth.register', event.user);
                    break;
                case 'logout':
                    analytics()
                        .track({ name: 'auth.logout' })
                        .catch(() => { });
                    analytics()
                        .reset?.()
                        .catch(() => { });
                    break;
                case 'error':
                    analytics()
                        .track({ name: 'auth.error', properties: { error: event.error } })
                        .catch(() => { });
                    break;
            }
        });
        cleanups.push(removeListener);
    }
    // Route changes
    if (options.router) {
        const removeListener = options.router.subscribe((location) => {
            analytics()
                .page({
                path: location.pathname,
                url: `${location.pathname}${location.search}${location.hash}`,
            })
                .catch(() => { });
        });
        cleanups.push(removeListener);
    }
    // HTTP errors
    if (options.httpClient) {
        const removeInterceptor = options.httpClient.addErrorInterceptor((error) => {
            analytics()
                .track({
                name: 'http.error',
                properties: {
                    status: error.status,
                    method: error.config?.method,
                    url: error.config?.url,
                    message: error.message,
                },
            })
                .catch(() => { });
            return error;
        });
        cleanups.push(removeInterceptor);
    }
    // App lifecycle (foreground/background transitions + deep links)
    if (options.lifecycleClient) {
        const removeStateListener = options.lifecycleClient.onAppStateChange((change) => {
            if (change.current === 'active' && change.previous !== 'active') {
                analytics()
                    .track({
                    name: 'app.foreground',
                    properties: { previous: change.previous },
                })
                    .catch(() => { });
            }
            else if (change.current === 'background' && change.previous !== 'background') {
                analytics()
                    .track({
                    name: 'app.background',
                    properties: { previous: change.previous },
                })
                    .catch(() => { });
            }
        });
        cleanups.push(removeStateListener);
        const removeUrlListener = options.lifecycleClient.onUrlOpen((url) => {
            analytics()
                .track({
                name: 'deeplink.open',
                properties: { url },
            })
                .catch(() => { });
        });
        cleanups.push(removeUrlListener);
    }
    // Push notifications (received + tapped)
    if (options.pushClient) {
        const removeReceivedListener = options.pushClient.onNotificationReceived((event) => {
            analytics()
                .track({
                name: 'push.received',
                properties: {
                    id: event.notification.id,
                    title: event.notification.title,
                    foreground: event.foreground,
                },
            })
                .catch(() => { });
        });
        cleanups.push(removeReceivedListener);
        const removeActionListener = options.pushClient.onNotificationAction((event) => {
            analytics()
                .track({
                name: 'push.tapped',
                properties: {
                    id: event.notification.id,
                    title: event.notification.title,
                    actionId: event.actionId,
                },
            })
                .catch(() => { });
        });
        cleanups.push(removeActionListener);
    }
    return () => {
        cleanups.forEach((fn) => fn());
    };
}
//# sourceMappingURL=auto-tracking.js.map